import { Injectable, LOCALE_ID, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService, AuthError } from './auth.service';
import { catchError, delay, retryWhen, flatMap, take, } from 'rxjs/operators';
import { throwError, Observable, of } from 'rxjs';
import { OperationError } from '../models/operation-error';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  static readonly URL = `${environment.api.url}/graphql`;

  constructor(
    private http: HttpClient,
    private router: Router,
    private auth: AuthService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  get(graphql: string, vars: any = {}, requiresAuth = true) {
    let headers: HttpHeaders;

    // Set auth token and sessionId
    if (requiresAuth) {
      const token = this.auth.getToken();

      if (!token) {
        return throwError({
          error: AuthError.Error,
          error_description: 'No token avaliable'
        });
      }

      headers = new HttpHeaders({
        'X-BO-SESSION-ID': token.sessionId ? token.sessionId : '',
        'Authorization': `Bearer ${token.accessToken}`
      });
    } else {
      headers = new HttpHeaders();
    }

    // Set locale
    if (this.locale === 'ar') {
      headers = headers.append('Accept-Language', 'ar');
    } else {
      headers = headers.append('Accept-Language', 'en');
    }

    // Set app type and version
    headers = headers.append('X-BO-CLIENT-TYPE', environment.api.type);
    headers = headers.append('X-BO-CLIENT-VERSION', environment.version);

    return this.http.post(ApiService.URL, {
      query: graphql,
      variables: vars
    }, {
      headers
    }).pipe(
      // TODO: DO We need retry and when
      // this.retry((err) => {
      //   return err.status === 500;
      // }),
      catchError((err) => {
        if (err && err.status === 401 || (err.error && err.error.error === 'invalid_token')) {
          return this.auth.refreshToken();
        } else {
          return throwError(err);
        }
      }),
      flatMap(response => {
        if (response.accessToken) {
          return this.get(graphql, vars, requiresAuth);
        } else {
          return of(response);
        }
      }),
      catchError(err => {
        if (err && err.error === AuthError.Error) {
          this.auth.logout();
          this.router.navigate(['/login'], { queryParams: {returnUrl: this.router.url} });
          return throwError(err);
        } else {
          return throwError(err);
        }
      })
    );
  }

  unifyErrors(cache = null) {
    return catchError <any, Observable <OperationError>> (err => {
      if (cache) { cache = null; }

      if (err && !err.errors) {
        err = {
          errors: [{
            message: 'Something went wrong, try again later',
            field: null
          }]
        };
      }

      return throwError(err);
    });
  }

  retry(codition: Function, times: number = 3) {
    return retryWhen(error => {
      return error.pipe(flatMap(err => {
        if (codition(err)) {
          return of(err).pipe(delay(500));
        }
        return throwError(err);
      })).pipe(
        take(times)
      );
    });
  }
}
